import React, { useState, useEffect } from "react";
import {
  Button,
  Tag,
  message,
  Divider,
  Row,
  Col,
  Input,
  Drawer,
  Form,
  Select,
  Switch,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PhoneOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { departmentAPIWithErrorHandling, departmentHelpers } from "../../../api/department/department";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import { confirmDelete } from "../../../components/common/confirmDialog";
import { actionColumnDef } from "../../../components/common/RowActions";
import ListToolbar, {
  selectVisibleColumns,
  useListToolbar,
} from "../../../components/common/ListToolbar";
import { neutral, spacing } from "../../../theme/tokens";

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = ["contact", "manager", "total_staff", "status"];

export default function DepartmentTable() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Modal States
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const {
    visibleFilters, setVisibleFilters,
    visibleColumns, setVisibleColumns,
  } = useListToolbar({
    defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
    defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
  });

  const fetchDepartments = async (page = 1, pageSize = 15, search = "", status = "") => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        search: search.trim(),
        status: status,
      };
      const response = await departmentAPIWithErrorHandling.getDepartments(params);
      const { departments: deptData, pagination: paginationData } = departmentHelpers.extractDepartmentList(response);

      setDepartments(deptData);
      setPagination({
        current: paginationData.current,
        pageSize: paginationData.pageSize,
        total: paginationData.total,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải danh sách phòng ban");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = (paginationConfig) => {
    fetchDepartments(paginationConfig.current, paginationConfig.pageSize, searchText, statusFilter);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchDepartments(1, pagination.pageSize, value, statusFilter);
  };

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
    fetchDepartments(1, pagination.pageSize, searchText, value);
  };

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("");
    fetchDepartments(1, pagination.pageSize, "", "");
  };

  const handleToggleStatus = async (id) => {
    try {
      await departmentAPIWithErrorHandling.toggleStatusDepartment(id);
      message.success("Cập nhật trạng thái thành công");
      fetchDepartments(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleDelete = async (record) => {
    const confirmed = await confirmDelete({ entity: "phòng ban", name: record.name });
    if (!confirmed) return;

    try {
      await departmentAPIWithErrorHandling.deleteDepartment(record.id);
      message.success("Xóa phòng ban thành công");
      fetchDepartments(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      message.error(error.message);
    }
  };

  const showModal = (record = null) => {
    setSelectedRecord(record);
    if (record) {
      form.setFieldsValue(record);
    } else {
      form.resetFields();
      form.setFieldsValue({ status: true, total_staff: 0 });
    }
    setIsModalOpen(true);
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      if (selectedRecord) {
        await departmentAPIWithErrorHandling.updateDepartment(selectedRecord.id, values);
        message.success("Cập nhật phòng ban thành công");
      } else {
        await departmentAPIWithErrorHandling.storeDepartment(values);
        message.success("Thêm phòng ban thành công");
      }
      setIsModalOpen(false);
      fetchDepartments(pagination.current, pagination.pageSize, searchText, statusFilter);
    } catch (error) {
      if (error.name !== "ValidationError") {
        message.error(error.message || "Lỗi xử lý yêu cầu");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ===== Định nghĩa cột =====
  const columnDefs = [
    {
      key: "info", label: "Thông tin phòng ban", fixed: true,
      column: {
        title: "Thông tin phòng ban",
        key: "info",
        width: 250,
        render: (_, record) => (
          <div>
            <div className="font-semibold text-blue-600">{record.name}</div>
            <div className="text-xs text-gray-400">Mã: {record.code}</div>
          </div>
        ),
      },
    },
    {
      key: "contact", label: "Liên hệ",
      column: {
        title: "Liên hệ",
        key: "contact",
        width: 200,
        render: (_, record) => (
          <div className="text-sm">
            {record.phone && (
              <div>
                <Tooltip title="Điện thoại">
                  <PhoneOutlined className="text-gray-400 mr-2" />
                  {record.phone}
                </Tooltip>
              </div>
            )}
            {record.email && (
              <div>
                <Tooltip title="Email">
                  <MailOutlined className="text-gray-400 mr-2" />
                  {record.email}
                </Tooltip>
              </div>
            )}
          </div>
        ),
      },
    },
    {
      key: "manager", label: "Quản lý",
      column: {
        title: "Quản lý",
        key: "manager",
        width: 180,
        render: (_, record) => (
          <div className="text-sm">
            {record.manager_name ? (
              <div>
                <UserOutlined className="text-gray-400 mr-2" />
                {record.manager_name}
              </div>
            ) : (
              <span className="text-gray-300 italic">Chưa có</span>
            )}
            <div className="text-xs text-gray-500">{record.manager_phone}</div>
          </div>
        ),
      },
    },
    {
      key: "total_staff", label: "Nhân sự",
      column: {
        title: "Nhân sự",
        dataIndex: "total_staff",
        key: "total_staff",
        width: 100,
        align: "center",
        render: (count) => <Tag color="blue">{count || 0}</Tag>,
      },
    },
    {
      key: "status", label: "Trạng thái",
      column: {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 120,
        render: (status, record) => (
          <Switch
            size="small"
            checked={status}
            onChange={() => handleToggleStatus(record.id)}
          />
        ),
      },
    },
    actionColumnDef(
      (record) => [
        { key: "edit", label: "Chỉnh sửa", icon: <EditOutlined />, tone: "edit", onClick: () => showModal(record) },
        { key: "delete", label: "Xóa", icon: <DeleteOutlined />, tone: "danger", onClick: () => handleDelete(record) },
      ],
      { width: 100 }
    ),
  ];

  // ===== Định nghĩa bộ lọc =====
  const filterDefs = [
    {
      key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Input.Search
          placeholder="Tìm tên, mã, số điện thoại..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
          enterButton={<SearchOutlined />}
          allowClear
        />
      ),
    },
    {
      key: "status", label: "Trạng thái", span: { xs: 24, sm: 12, md: 8 },
      field: (
        <Select
          style={{ width: "100%" }}
          placeholder="Lọc trạng thái"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          allowClear
        >
          <Select.Option value="true">Đang hoạt động</Select.Option>
          <Select.Option value="false">Tạm ngưng</Select.Option>
        </Select>
      ),
    },
  ];

  const columns = selectVisibleColumns(columnDefs, visibleColumns);
  const hasActiveFilter = !!(searchText || statusFilter);

  return (
    <BrandedPageLayout
      title="Phòng ban"
      subtitle="Quản lý danh sách phòng ban, liên hệ và nhân sự."
      breadcrumbItems={[{ title: "Phòng ban" }, { title: "Danh sách" }]}
    >
      <ListToolbar
        filterDefs={filterDefs}
        visibleFilters={visibleFilters}
        onChangeVisibleFilters={setVisibleFilters}
        columnDefs={columnDefs}
        visibleColumns={visibleColumns}
        onChangeVisibleColumns={setVisibleColumns}
        defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
        hasActiveFilter={hasActiveFilter}
        onClearFilter={handleReset}
        onReload={() => fetchDepartments(pagination.current, pagination.pageSize, searchText, statusFilter)}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>
            Thêm phòng ban
          </Button>
        }
      />

      <ListTableCard
        columns={columns}
        dataSource={departments}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        emptyText="Không có dữ liệu"
      />

      <Drawer
        title={
          <div className="flex items-center gap-2">
            {selectedRecord ? <EditOutlined className="text-blue-500" /> : <PlusOutlined className="text-green-500" />}
            <span>{selectedRecord ? "Chỉnh sửa phòng ban" : "Thêm phòng ban mới"}</span>
          </div>
        }
        placement="right"
        width="min(720px, 100vw)"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        destroyOnClose
        styles={{ body: { background: neutral[50], padding: spacing[6] } }}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: spacing[2] }}>
            <Button onClick={() => setIsModalOpen(false)} disabled={submitting}>
              Hủy
            </Button>
            <Button type="primary" loading={submitting} onClick={handleModalSubmit}>
              {selectedRecord ? "Cập nhật" : "Thêm mới"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" className="mt-4 pt-4 border-t">
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Tên phòng ban"
                rules={[{ required: true, message: "Vui lòng nhập tên phòng ban!" }]}
              >
                <Input placeholder="Ví dụ: Phòng Quản lý Đô thị" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Mã phòng ban (Tùy chọn)"
                tooltip="Bỏ trống để hệ thống tự tạo"
              >
                <Input placeholder="Ví dụ: QLDT" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Số điện thoại liên hệ"
              >
                <Input placeholder="Số điện thoại bàn hoặc di động" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email liên hệ"
                rules={[{ type: "email", message: "Định dạng email không hợp lệ!" }]}
              >
                <Input placeholder="email@phuong.gov.vn" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="address"
                label="Địa chỉ / vị trí làm việc"
                tooltip="Hiện thành một dòng riêng có icon định vị trên mini app"
              >
                <Input placeholder="Ví dụ: Tầng trệt, trụ sở UBND Phường" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="working_hours"
                label="Thời gian làm việc"
                tooltip="Nhập tự do. Trước đây phải nhồi vào ô Mô tả nên mini app không tách ra được"
              >
                <Input placeholder="Ví dụ: Sáng 07:30-11:30 | Chiều 13:30-17:00 (T2-T6)" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left" plain className="text-xs text-gray-400">Thông tin nhân sự</Divider>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="manager_name"
                label="Tên trưởng phòng / người phụ trách"
              >
                <Input placeholder="Họ và tên" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="manager_phone"
                label="Số điện thoại người phụ trách"
              >
                <Input placeholder="SĐT cá nhân" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="total_staff"
                label="Số lượng nhân viên"
              >
                <Input type="number" min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Trạng thái"
                valuePropName="checked"
              >
                <Switch checkedChildren="Hoạt động" unCheckedChildren="Tạm ngưng" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Mô tả chức năng nhiệm vụ"
            tooltip="Chỉ mô tả chức năng. Địa chỉ và giờ làm việc đã có ô riêng ở trên"
          >
            <Input.TextArea rows={3} placeholder="Nhập mô tả ngắn gọn về chức năng, nhiệm vụ..." />
          </Form.Item>
        </Form>
      </Drawer>
    </BrandedPageLayout>
  );
}
